/**
 * 💬 Feedback: a star rating and a short message, sent to Formspree (https://formspree.io), which
 * emails every submission to you and keeps them on its dashboard. Same service as Rashtra.
 *
 * The endpoint comes from the server (GES_FORMSPREE_ENDPOINT, read from /api/meta) so it can be
 * changed on Render without touching code; GES_FEEDBACK_DEFAULT is used when that isn't set.
 * Every entry is also kept in this browser (localStorage), and if sending fails the thank-you
 * screen offers to copy it, so nothing a player writes is lost.
 */
const GES_FEEDBACK_DEFAULT = "https://formspree.io/f/xqpeojzn"; // the "GES Feedback" form
const GES_FEEDBACK_KEY = "ges.feedback";
const gesFbEsc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function gesFeedbackEndpoint() {
  const fromServer = window.gesMeta && window.gesMeta.feedback_endpoint;
  return fromServer || GES_FEEDBACK_DEFAULT;
}

/** What the player was doing, so a message like "this is confusing" can be acted on. */
function gesFeedbackContext() {
  const text = (id) => { const el = document.getElementById(id); return el ? el.textContent.trim() : ""; };
  const tab = document.querySelector(".sidebar .tab.active");
  return {
    company: text("db-company-name"),
    month: text("kpi-month"),
    tab: tab ? tab.dataset.tab : "",
    account: window.gesAuthToken ? "google" : "guest",
    screen: `${window.innerWidth}x${window.innerHeight}`,
    device: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || "") ? "phone/tablet" : "computer",
  };
}

function gesKeepLocal(entry) {
  try {
    const all = JSON.parse(localStorage.getItem(GES_FEEDBACK_KEY) || "[]");
    all.push(entry);
    localStorage.setItem(GES_FEEDBACK_KEY, JSON.stringify(all.slice(-20)));
  } catch (e) { /* private mode: the copy button still works */ }
}

const GES_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What goes to Formspree. Formspree checks a field named "email" and rejects anything that isn't a real
 *  address (it refused "(not given)", so feedback without an email never arrived): send it only when the
 *  player typed a valid one. Then it's also the reply-to address on the email you receive. */
function gesFeedbackBody(entry) {
  const body = {
    _subject: `GES feedback: ${entry.rating}/5 stars`,
    rating: entry.rating,
    liked: entry.liked || "(none given)",
    improve: entry.improve || "(none given)",
    ...entry.context,
    date: entry.date,
  };
  if (entry.email && GES_EMAIL_RE.test(entry.email)) body.email = entry.email;
  return body;
}

async function gesSendFeedback(entry) {
  try {
    const res = await fetch(gesFeedbackEndpoint(), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(gesFeedbackBody(entry)),
    });
    return res.ok;
  } catch (e) { return false; }
}

function gesOpenFeedback() {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `<div class="modal fb-modal" role="dialog" aria-label="Tell us what you think">
      <h3>💬 Tell us what you think</h3>
      <p class="fb-sub">You're one of the first players. What you write here decides what gets built next.</p>
      <div class="fb-stars" role="radiogroup" aria-label="Your rating">
        ${[1, 2, 3, 4, 5].map((n) => `<button type="button" class="fb-star" data-star="${n}" role="radio" aria-checked="false" aria-label="${n} star${n > 1 ? "s" : ""}">★</button>`).join("")}
      </div>
      <div class="field"><label for="fb-liked">What did you enjoy?</label>
        <textarea id="fb-liked" rows="2" maxlength="1000" placeholder="e.g. the Shark Tank, the money jars…"></textarea></div>
      <div class="field"><label for="fb-improve">What was confusing, or what's missing?</label>
        <textarea id="fb-improve" rows="3" maxlength="1500" placeholder="Anything at all, big or small."></textarea></div>
      <div class="field"><label for="fb-email">Email <span class="fb-opt">(optional, only if you'd like a reply)</span></label>
        <input id="fb-email" type="email" maxlength="120" placeholder="you@example.com"></div>
      <div class="fb-error hidden" id="fb-error">Pick a star rating first.</div>
      <div class="modal-actions">
        <button type="button" data-fb="cancel">Not now</button>
        <button type="button" class="primary" data-fb="send">Send feedback</button>
      </div>
    </div>`;
  document.body.appendChild(backdrop);
  let rating = 0;
  const close = () => backdrop.remove();
  backdrop.addEventListener("click", async (e) => {
    if (e.target === backdrop) return close();
    const star = e.target.closest("[data-star]");
    if (star) {
      rating = Number(star.dataset.star);
      backdrop.querySelectorAll("[data-star]").forEach((b) => {
        const on = Number(b.dataset.star) <= rating;
        b.classList.toggle("on", on);
        b.setAttribute("aria-checked", String(Number(b.dataset.star) === rating));
      });
      backdrop.querySelector("#fb-error").classList.add("hidden");
      return;
    }
    const act = e.target.closest("[data-fb]");
    if (!act) return;
    if (act.dataset.fb === "cancel") return close();
    if (act.dataset.fb === "send") {
      const err = backdrop.querySelector("#fb-error");
      const typedEmail = backdrop.querySelector("#fb-email").value.trim();
      if (!rating) { err.textContent = "Pick a star rating first."; err.classList.remove("hidden"); return; }
      if (typedEmail && !GES_EMAIL_RE.test(typedEmail)) {
        err.textContent = "That email doesn't look right. Fix it, or leave it empty."; err.classList.remove("hidden"); return;
      }
      const entry = {
        rating,
        liked: backdrop.querySelector("#fb-liked").value.trim(),
        improve: backdrop.querySelector("#fb-improve").value.trim(),
        email: backdrop.querySelector("#fb-email").value.trim(),
        context: gesFeedbackContext(),
        date: new Date().toISOString(),
      };
      act.disabled = true;
      act.textContent = "Sending…";
      gesKeepLocal(entry);
      const sent = await gesSendFeedback(entry);
      if (window.gesTrack) window.gesTrack("feedback_sent", { rating, delivered: sent ? 1 : 0 });
      close();
      gesFeedbackThanks(entry, sent);
    }
  });
}

function gesFeedbackThanks(entry, sent) {
  const text = `GES feedback (${entry.rating}/5)\nEnjoyed: ${entry.liked || "-"}\nImprove: ${entry.improve || "-"}`;
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `<div class="modal fb-modal" role="dialog" aria-label="Thank you">
      <h3>🙏 Thank you!</h3>
      ${sent
        ? `<p class="fb-sub">Your feedback reached us. It really helps.</p>`
        : `<p class="fb-sub">We couldn't send it right now (no connection?). It's saved on this device. You can copy it and send it to us another way.</p>
           <textarea readonly rows="4" class="fb-copy">${gesFbEsc(text)}</textarea>`}
      <div class="modal-actions">
        ${sent ? "" : `<button type="button" data-fb="copy">Copy</button>`}
        <button type="button" class="primary" data-fb="done">Back to the game</button>
      </div>
    </div>`;
  document.body.appendChild(backdrop);
  backdrop.addEventListener("click", async (e) => {
    const act = e.target.closest("[data-fb]");
    if (e.target === backdrop || (act && act.dataset.fb === "done")) return backdrop.remove();
    if (act && act.dataset.fb === "copy") {
      try { await navigator.clipboard.writeText(text); act.textContent = "Copied ✓"; }
      catch (err) { backdrop.querySelector(".fb-copy").select(); }
    }
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  window.gesOpenFeedback = gesOpenFeedback;
  document.addEventListener("click", (e) => {
    if (e.target.closest && e.target.closest("[data-open-feedback]")) gesOpenFeedback();
  });
}
